import dayjs, { Dayjs } from "dayjs";
import styled from "@emotion/styled";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Typography from "@mui/material/Typography";

import { BaseCalendar, LoadingSpinner } from "@/components";

import { useCustomTranslation } from "@/utils/useCustomTranslation";

interface ScheduleListCalendarProps {
  selectedDate: Dayjs;
  onDateChange: (date: Dayjs) => void;
  onMonthChange: (date: Dayjs) => void;
  scheduleDates: Dayjs[];
  isLoading: boolean;
}

const LegendFooter = () => {
  const { t } = useCustomTranslation();

  return (
    <FooterWrapper>
      <LegendItem>
        <FiberManualRecordIcon
          color="primary"
          sx={{
            // B3_R 스타일 적용
            fontSize: 12,
            fontWeight: 400,
            lineHeight: "18px",
            letterSpacing: "-0.4px",
          }}
        />
        <Typography
          color="primary"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "20px",
            letterSpacing: "-0.4px",
            textAlign: "left",
            textUnderlinePosition: "from-font",
            textDecorationSkipInk: "none",
          }}
        >
          {t("class_exists")}
        </Typography>
      </LegendItem>
    </FooterWrapper>
  );
};

export const ScheduleListCalendar = ({
  selectedDate,
  onDateChange,
  scheduleDates,
  onMonthChange: handleMonthChange,
  isLoading,
}: ScheduleListCalendarProps) => {
  const CustomDay = (day: Dayjs) => ({
    hasSchedule: scheduleDates.some((date) => date.isSame(day, "day")),
  });

  if (isLoading) {
    <LoadingSpinner message="스케줄을 불러오는 중입니다." />;
  }

  return (
    <CalendarContainer>
      <DateSelectorHeader>
        <SelectDateText>SELECT DATE</SelectDateText>
        <SelectedDateText>
          {dayjs(selectedDate).format("M월 D일 dddd")}
        </SelectedDateText>
      </DateSelectorHeader>
      <BaseCalendar
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        onMonthChange={handleMonthChange}
        CustomDay={CustomDay}
        CustomFooter={LegendFooter}
      />
    </CalendarContainer>
  );
};

const CalendarContainer = styled.div`
  width: 320px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
  background: white;

  /* BaseCalendar의 border 제거 */
  > div {
    border: none;
  }
`;

const DateSelectorHeader = styled.div`
  width: 100%;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
`;

const SelectDateText = styled(Typography)`
  //styleName: typography/overline;
  font-family: Roboto;
  font-size: 12px;
  font-weight: 400;
  line-height: 31.92px;
  letter-spacing: 1px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: ${({ theme }) => theme.colors.brand.secondary};
`;

const SelectedDateText = styled(Typography)`
  //styleName: typography/h4;
  font-family: Roboto;
  font-size: 34px;
  font-weight: 400;
  line-height: 41.99px;
  letter-spacing: 0.25px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
`;

const FooterWrapper = styled.div`
  width: 100%;
  height: 48px;
  padding: 14px 24px;
  display: flex;
  align-items: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
